import { Color, Spacing } from '@persona-id/design-tokens';
import {
  ArrowLeftIcon,
  BellIcon,
  BriefcaseIcon,
  ChevronRightIcon,
  CircleCheckIcon,
  CodeIcon,
  DocumentIcon,
  GraphIcon,
  GridIcon,
  IdFrontIcon,
  ListIcon,
  MailIcon,
  MergeIcon,
  ToolIcon,
  UsersCheckIcon,
  WorkflowsIcon,
} from '@persona-id/design-tokens/icons';
import { isEqual } from 'lodash';
import { groupBy, kebabCase, last, sortBy, startCase } from 'lodash/fp';
import { useCallback, useContext, useEffect, useMemo, useState, VFC } from 'react';
// Our custom `<ReactSelect />` only supports string-based values
// eslint-disable-next-line no-restricted-imports
import Select, {
  components,
  ControlProps,
  GroupBase,
  OptionProps,
  StylesConfig,
} from 'react-select';
import styled, { css, ThemeContext } from 'styled-components';

import useHasAccessForTierDeterminator from 'dashboard/apps/InquiryTemplateEditor/hooks/useHasAccessForTierDeterminator';
import { EditorAccessTier } from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import InquiriesGatingSticker, {
  EnhancedGatingStickerType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiriesGatingSticker';
import {
  defaultReactSelectStyles,
  OptionType,
  useCustomReactSelectComponents,
} from 'dashboard/components/ReactSelect';
import Tooltip from 'dashboard/components/Tooltip';
import {
  ActionGroup,
  ActionMetaData,
  ActionMetaDataMap,
  SubAction,
  SubActionGroup,
} from 'dashboard/components/WorkflowControlPanel/StepConfigAction/selectActionUtils';
import { Trigger } from 'dashboard/models/trigger';
import {
  AllWorkflowAction,
  ENHANCED_ACTIONS,
  INQUIRY_BASIC_ACTIONS,
  INQUIRY_ENHANCED_ACTIONS,
  NullWorkflowStepActionKey,
  WorkflowStepActionKey,
} from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';
import { Paragraph, Span } from 'design-system/components/Text';
import useCurrentEnvironmentType from 'lib/hooks/useCurrentEnvironmentType';
import usePrevious from 'lib/hooks/usePrevious';
import { ApiModel } from 'lib/interfaces/api';

const GroupRow = styled.div<{ justify: 'space-between' | 'flex-start' }>`
  display: flex;
  justify-content: ${({ justify }) => justify};
  align-items: center;
`;

const IconAndLabel = styled.div`
  display: flex;
  align-items: center;
`;

const IconContainer = styled.div`
  display: flex;
  height: 16px;
  width: 16px;

  svg {
    width: 16px;
    height: 16px;
  }

  color: ${({ theme }) => theme.color.text.primary};
`;

const StyledIconChevron = styled(ChevronRightIcon)`
  color: ${({ theme }) => theme.color.text.primary};
`;

const MainText = styled(Span)<{ disabled?: boolean }>`
  display: flex;
  color: ${({ theme }) => theme.color.text.primary};
  ${(p) =>
    p.disabled &&
    css`
      color: ${Color.CoreSlate200};
    `};
`;
const CustomOption = (props: OptionProps<ActionOption>) => {
  const index = last(props.innerProps?.id?.split('-') || []);
  const id = props.selectProps.id != null ? `${props.selectProps.id}-option-${index}` : undefined;

  if (props.data.value === NavigationOptions.BACK) {
    return (
      <components.Option {...props} innerProps={{ ...props.innerProps, id }}>
        <GroupRow justify='flex-start'>
          <IconContainer>{props.data.icon}</IconContainer>
          <Spacer width={1} height={0} />
          <MainText>{props.data.label}</MainText>
        </GroupRow>
      </components.Option>
    );
  }

  return (
    <components.Option {...props} innerProps={{ ...props.innerProps, id }}>
      <GroupRow justify='space-between'>
        <IconAndLabel>
          {props.data.icon && !props.isDisabled && (
            <>
              <IconContainer>{props.data.icon}</IconContainer>
              <Spacer width={2} height={0} />
            </>
          )}
          <MainText disabled={props.isDisabled} id={`main-text-${id}`}>
            {props.data.label}
            {props.isDisabled && (
              <>
                <Spacer width={2} height={0} />
                {props.data.icon}
              </>
            )}
          </MainText>
        </IconAndLabel>
        {props.data.isGroupMenu && <StyledIconChevron />}
      </GroupRow>
    </components.Option>
  );
};

const CustomControl = (props: ControlProps<ActionOption> & { onControlClick: () => void }) => {
  const { onControlClick, ...otherProps } = props;
  return (
    <div id={props.selectProps.id} onClick={onControlClick}>
      <components.Control {...otherProps} />
    </div>
  );
};

const GROUP_ICONS: Record<ActionGroup, JSX.Element> = {
  [ActionGroup.Analytics]: <BellIcon />,
  [ActionGroup.Accounts]: <UsersCheckIcon />,
  [ActionGroup.Case]: <BriefcaseIcon />,
  [ActionGroup.Communicate]: <MailIcon />,
  [ActionGroup.Compute]: <CodeIcon />,
  [ActionGroup.Graph]: <GraphIcon />,
  [ActionGroup.Inquiry]: <IdFrontIcon />,
  [ActionGroup.InquiryFlow]: <WorkflowsIcon />,
  [ActionGroup.List]: <ListIcon />,
  [ActionGroup.Marketplace]: <GridIcon />,
  [ActionGroup.Other]: <ToolIcon />,
  [ActionGroup.Report]: <DocumentIcon />,
  [ActionGroup.Transaction]: <MergeIcon />,
  [ActionGroup.Verification]: <CircleCheckIcon />,
};

const customStyles: StylesConfig<ActionOption> = {
  option: (styles) => ({
    ...styles,
    display: 'block',
    padding: `${Spacing[2]}`,
  }),
};

enum NavigationOptions {
  BACK = 'back',
}
const BACK_OPTION: ActionOption = {
  label: startCase(NavigationOptions.BACK),
  value: NavigationOptions.BACK,
  icon: <ArrowLeftIcon />,
};

type ActionOptionType = AllWorkflowAction | ActionGroup | SubActionGroup | NavigationOptions.BACK;

interface ActionOption extends OptionType<ActionOptionType> {
  label: string;
  value: ActionOptionType;
  group?: ActionGroup;
  subGroup?: SubAction;
  icon?: JSX.Element;
  isGroupMenu?: boolean;
}

interface ActionSelectProps {
  actionMetaDataMap: ActionMetaDataMap;
  genericActionMetaData: ActionMetaData[];
  actions: AllWorkflowAction[];
  trigger?: Trigger;
  disabled: boolean;
  value: AllWorkflowAction;
  hasWorkflowsFree: boolean;
  hasWorkflowsBasic: boolean;
  hasWorkflowsEnhanced: boolean;
  onActionSelect: (selectedAction: AllWorkflowAction) => void;
}

const ActionSelect: VFC<ActionSelectProps> = ({
  actionMetaDataMap,
  genericActionMetaData,
  actions,
  trigger,
  value,
  disabled,
  onActionSelect,
  hasWorkflowsBasic,
  hasWorkflowsFree,
  hasWorkflowsEnhanced,
}) => {
  const hasAccessForTier = useHasAccessForTierDeterminator();
  const hasInquiryBasic = hasAccessForTier(EditorAccessTier.InquiriesBasic);
  const hasInquiryEnhanced = hasAccessForTier(EditorAccessTier.InquiriesEnhanced);

  // This code is slightly redundant but very explicit to make it clear
  // what actions are disabled per product feature
  // See go/workflows-self-serve
  const isOptionDisabled = useCallback(
    (option: ActionOption) => {
      if (trigger?.type === ApiModel.TriggerDirect) {
        if (hasInquiryEnhanced) {
          return false;
        }
        if (hasInquiryBasic) {
          return INQUIRY_ENHANCED_ACTIONS.some(
            (opt) => typeof option.value !== 'string' && opt === option.value.key,
          );
        } else {
          return (
            INQUIRY_ENHANCED_ACTIONS.some(
              (opt) => typeof option.value !== 'string' && opt === option.value.key,
            ) ||
            INQUIRY_BASIC_ACTIONS.some(
              (opt) => typeof option.value !== 'string' && opt === option.value.key,
            )
          );
        }
      } else {
        if (hasWorkflowsEnhanced) {
          return false;
        }

        if (hasWorkflowsBasic) {
          return ENHANCED_ACTIONS.some(
            (opt) => typeof option.value !== 'string' && opt === option.value.key,
          );
        }

        if (hasWorkflowsFree) {
          return false;
        }
      }
    },
    [
      hasInquiryBasic,
      hasInquiryEnhanced,
      hasWorkflowsBasic,
      hasWorkflowsEnhanced,
      hasWorkflowsFree,
      trigger?.type,
    ],
  );
  const { name: environmentName } = useCurrentEnvironmentType();
  const [allOptions, actionOptionsMap, groupOptions, subGroupOptions] = useMemo(() => {
    const options: ActionOption[] = sortBy(
      'label',
      actions.map((action) => {
        let metaData: ActionMetaData;
        if (action.key === WorkflowStepActionKey.RunHttpPlatformIntegration) {
          metaData = genericActionMetaData.find((meta) =>
            isEqual(meta.integration, action.params.integration),
          ) as ActionMetaData;
        } else {
          metaData =
            genericActionMetaData.find((meta) => meta.key === action.key) ||
            actionMetaDataMap[action.key];
        }
        return {
          ...metaData,
          value: action,
          id: `select-action__${kebabCase(metaData.label)}__${environmentName}`,
        };
      }),
    );

    const optionsAll = options.map((option) => {
      return isOptionDisabled(option)
        ? {
            ...option,
            ...{
              icon: (
                <Tooltip
                  trigger='hover'
                  placement='bottom'
                  tooltip={
                    <Paragraph color='primary' size='md'>
                      This feature is not available on your plan.
                    </Paragraph>
                  }
                >
                  <InquiriesGatingSticker
                    dataMarketingIdSuffix='workflows-actions-select'
                    stickerType={EnhancedGatingStickerType.Limited}
                  />
                </Tooltip>
              ),
            },
          }
        : option;
    });

    const optionsSubGroupLevel: ActionOption[] = optionsAll.filter((option) => {
      return option.subGroup;
    });
    const mapSubGroupOptions = groupBy('subGroup.value', optionsSubGroupLevel);

    const mapGroupToSub: Record<string, SubAction[]> = {};
    optionsSubGroupLevel.forEach((option) => {
      const groupType = option.group;
      const subGroupType = option.subGroup;
      if (groupType == null || subGroupType == null) {
        return;
      }
      if (!mapGroupToSub[groupType]) {
        mapGroupToSub[groupType] = [subGroupType];
      } else if (!mapGroupToSub[groupType].some((ele) => isEqual(ele, subGroupType))) {
        mapGroupToSub[groupType].push(subGroupType);
      }
    });

    const optionsGroupLevel: ActionOption[] = optionsAll.filter((option) => !option.subGroup);

    const mapActionOptions = groupBy('group', optionsGroupLevel);

    const optionsGroup: ActionOption[] = sortBy(
      'label',
      Object.keys(groupBy('group', optionsAll)).map((groupKey) => ({
        icon: GROUP_ICONS[groupKey as ActionGroup],
        label: startCase(groupKey.valueOf()),
        value: groupKey as ActionGroup,
        isGroupMenu: true,
      })),
    );
    for (const [group, subGroupArr] of Object.entries(mapGroupToSub)) {
      const subGroupArrWithFlag = subGroupArr.map((ele) => {
        return { ...ele, isGroupMenu: true };
      });
      if (!mapActionOptions[group]) {
        mapActionOptions[group] = subGroupArrWithFlag;
      } else {
        mapActionOptions[group].push(...subGroupArrWithFlag);
      }
    }
    for (const [group, options] of Object.entries(mapActionOptions)) {
      mapActionOptions[group] = sortBy('label', options);
    }
    return [optionsAll, mapActionOptions, optionsGroup, mapSubGroupOptions];
  }, [actions, actionMetaDataMap, environmentName, isOptionDisabled]);

  const getNestedOptions = useCallback(
    (value: ActionOption['value']) => {
      const optionMatch = allOptions.find((option) => {
        if (
          typeof value !== 'string' &&
          value.key === WorkflowStepActionKey.RunHttpPlatformIntegration &&
          typeof option.value !== 'string' &&
          option.value.key === WorkflowStepActionKey.RunHttpPlatformIntegration
        ) {
          return isEqual(option.value.params.integration, value.params.integration);
        } else if (!(typeof value === 'string' || typeof option.value === 'string')) {
          return isEqual(option.value.key, value.key);
        } else {
          return false;
        }
      });

      const actionOptions = optionMatch?.subGroup
        ? subGroupOptions[optionMatch?.subGroup.value]
        : optionMatch?.group && actionOptionsMap[optionMatch?.group];
      if (actionOptions == null) {
        return [BACK_OPTION];
      } else {
        return [BACK_OPTION, ...actionOptions];
      }
    },
    [actionOptionsMap, allOptions, subGroupOptions],
  );

  const [actionOptions, setActionOptions] = useState(groupOptions);
  const [isOpen, setIsOpen] = useState(false);
  const previousValue = usePrevious(value);

  useEffect(() => {
    if (value.key !== NullWorkflowStepActionKey.NullKey) {
      setActionOptions(getNestedOptions(value));
    }
  }, [value, previousValue, getNestedOptions]);

  const onActionChange = useCallback(
    (value: ActionOption['value']) => {
      if (typeof value === 'string' && subGroupOptions[value]) {
        setActionOptions([BACK_OPTION, ...subGroupOptions[value]]);
      } else if (typeof value === 'string' && actionOptionsMap[value]) {
        setActionOptions([BACK_OPTION, ...actionOptionsMap[value]]);
      } else if (typeof value === 'string' && value === NavigationOptions.BACK) {
        setActionOptions(groupOptions);
      } else if (typeof value !== 'string') {
        setActionOptions(getNestedOptions(value));
        onActionSelect(value);
        setIsOpen(false);
      }
    },
    [subGroupOptions, actionOptionsMap, groupOptions, getNestedOptions, onActionSelect],
  );

  const onInputChange = useCallback(
    (val: string, e: { action: string }) => {
      if (e.action !== 'input-change') {
        return;
      }
      if (val && actionOptions.length !== allOptions.length) {
        setActionOptions(allOptions);
      } else if (!val) {
        setActionOptions(groupOptions);
      }
    },
    [actionOptions, groupOptions, allOptions],
  );

  const styles = defaultReactSelectStyles({ customStyles, theme: useContext(ThemeContext) });

  const currentActionOption = allOptions.find((option) => {
    if (
      !(typeof option.value === 'string') &&
      value.key === WorkflowStepActionKey.RunHttpPlatformIntegration &&
      option.value.key === WorkflowStepActionKey.RunHttpPlatformIntegration
    ) {
      return isEqual(option.value.params.integration, value.params.integration);
    } else if (!(typeof option.value === 'string')) {
      return isEqual(option.value.key, value.key);
    } else {
      return false;
    }
  });
  return (
    <Select<ActionOption>
      autoFocus={value.key === NullWorkflowStepActionKey.NullKey}
      id='action-select'
      value={currentActionOption}
      controlShouldRenderValue={currentActionOption != null}
      isDisabled={disabled}
      aria-label='Select workflow action'
      isSearchable
      onInputChange={onInputChange}
      menuIsOpen={isOpen}
      styles={styles}
      placeholder='Find Action...'
      options={actionOptions}
      onChange={(actionOption, _meta) => actionOption && onActionChange(actionOption.value)}
      onFocus={() => setIsOpen(true)}
      onBlur={(e) => {
        if (e.relatedTarget) {
          return;
        }
        setIsOpen(false);
      }}
      isOptionDisabled={(option) => isOptionDisabled(option) || false}
      components={useCustomReactSelectComponents<
        ActionOptionType,
        ActionOption,
        false,
        GroupBase<ActionOption>
      >({
        Option: CustomOption,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        Control: (props) => <CustomControl {...props} onControlClick={() => setIsOpen(true)} />,
      })}
    />
  );
};

export default ActionSelect;
